// ABOUTME: Autosave module for project forms - automatically saves drafts as users type
// ABOUTME: Provides debounced saving, offline support, and visual feedback for form changes

class AutosaveManager {
  constructor(formId, projectId) {
    this.form = document.getElementById(formId);
    this.projectId = projectId; // Can be null for new projects
    this.saveTimeout = null;
    this.isOnline = navigator.onLine;
    this.pendingChanges = false;
    this.manualSaveRequired = false;
    this.lastSaveTime = null;
    this.isSaving = false;
    
    // Simple i18n translations
    this.translations = {
      sk: {
        unsavedChanges: 'Neuložené zmeny',
        saving: 'Ukladá sa...',
        saved: 'Uložené',
        saveFailed: 'Uloženie zlyhalo',
        offline: 'Offline - zmeny sa uložia po obnovení pripojenia',
        confirmLeave: 'Máte neuložené zmeny. Naozaj chcete odísť?'
      },
      en: {
        unsavedChanges: 'Unsaved changes',
        saving: 'Saving...',
        saved: 'Saved',
        saveFailed: 'Save failed',
        offline: 'Offline - changes will be saved when connection is restored',
        confirmLeave: 'You have unsaved changes. Are you sure you want to leave?'
      }
    };
    
    // Get current locale from HTML lang attribute or default to 'sk'
    this.currentLocale = document.documentElement.lang || 'sk';
    
    // Track whether this is a published project — summary changes need manual save
    this.isPublished = this.form?.dataset.published === 'true';

    // DEFERRED CREATION: Initialize if we have a form (project ID can be null for new projects)
    if (this.form) {
      this.initializeEventListeners();
      this.setupOnlineOfflineHandlers();
      this.createSaveIndicator();
    }
  }
  
  // Simple translation helper
  t(key) {
    return this.translations[this.currentLocale]?.[key] || this.translations['en'][key] || key;
  }
  
  initializeEventListeners() {
    // Debounced autosave on input changes
    this.form.addEventListener('input', (e) => {
      if (this.shouldIgnoreField(e.target)) return;

      // Summary on published projects: track separately, skip autosave debounce
      if (this.isPublished && this.isSummaryField(e.target)) {
        this.manualSaveRequired = true;
        console.log('[autosave:input] manualSaveRequired=true (summary on published), field:', e.target.name);
        return;
      }

      this.pendingChanges = true;
      console.log('[autosave:input] pendingChanges=true, field:', e.target.name || e.target.id);
      this.clearSaveTimeout();

      // Debounce save for 2 seconds
      this.saveTimeout = setTimeout(() => {
        console.log('[autosave:debounce] timer fired, calling performAutosave');
        this.performAutosave();
      }, 2000);
    });

    // Also listen for changes (for dropdowns/checkboxes)
    this.form.addEventListener('change', (e) => {
      if (this.shouldIgnoreField(e.target)) return;

      // Summary on published projects: track separately, skip autosave debounce
      if (this.isPublished && this.isSummaryField(e.target)) {
        this.manualSaveRequired = true;
        console.log('[autosave:change] manualSaveRequired=true (summary on published), field:', e.target.name);
        return;
      }

      this.pendingChanges = true;
      console.log('[autosave:change] pendingChanges=true, field:', e.target.name || e.target.id);
      this.clearSaveTimeout();

      // Debounce save for 2 seconds
      this.saveTimeout = setTimeout(() => {
        console.log('[autosave:debounce] timer fired, calling performAutosave');
        this.performAutosave();
      }, 2000);
    });

    // Clear pending changes when form is submitted normally (secondary safety net)
    this.form.addEventListener('submit', (e) => {
      console.log('[autosave:submit] submit event fired! Clearing state. pendingChanges was:', this.pendingChanges, 'manualSaveRequired was:', this.manualSaveRequired);
      this.pendingChanges = false;
      this.manualSaveRequired = false;
      this.clearSaveTimeout();
      this.hideSaveIndicator();
    });

    // Save before page unload
    window.addEventListener('beforeunload', (e) => {
      const hasUnsaved = this.pendingChanges || this.manualSaveRequired;
      console.log('[autosave:beforeunload] pendingChanges:', this.pendingChanges, 'manualSaveRequired:', this.manualSaveRequired, 'isSaving:', this.isSaving);
      if (hasUnsaved && !this.isSaving) {
        console.log('[autosave:beforeunload] BLOCKING navigation — showing dialog');
        // Attempt synchronous save for autosaveable fields only
        if (this.pendingChanges) {
          this.performAutosave(true);
        }
        e.preventDefault();
        e.returnValue = this.t('confirmLeave');
      } else {
        console.log('[autosave:beforeunload] allowing navigation (no pending changes or saving in progress)');
      }
    });
  }
  
  shouldIgnoreField(element) {
    // Don't autosave file inputs, publish button, or system fields
    return element.type === 'file' ||
           element.name === 'authenticity_token' ||
           element.name === 'publish' ||
           element.name === 'draft' ||
           element.classList.contains('no-autosave');
  }

  // Summary (title) field needs manual save on published projects to avoid
  // triggering admin approval notifications on every autosave keystroke.
  isSummaryField(element) {
    return element.name === 'project[summary]';
  }
  
  async performAutosave(synchronous = false) {
    console.log('[autosave:performAutosave] called. synchronous:', synchronous, 'isOnline:', this.isOnline, 'pendingChanges:', this.pendingChanges, 'isSaving:', this.isSaving);
    if (!this.isOnline || !this.pendingChanges || this.isSaving) {
      console.log('[autosave:performAutosave] skipping — guard condition hit');
      return;
    }

    this.isSaving = true;
    const formData = new FormData(this.form);
    
    // Ensure we're saving as draft for autosave
    formData.set('draft', 'true');
    formData.append('autosave', 'true');
    
    try {
      // DEFERRED CREATION: Handle both new projects (no ID) and existing projects
      const url = this.projectId ? `/projects/${this.projectId}` : '/projects';
      const method = this.projectId ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      });
      
      if (response.ok) {
        console.log('[autosave:performAutosave] save succeeded, clearing pendingChanges');
        // Check if this was the first save and we got a project ID back
        if (!this.projectId && response.headers.get('content-type')?.includes('application/json')) {
          const responseData = await response.json();
          if (responseData.project_id) {
            this.projectId = responseData.project_id;
            // Update form action for future submissions
            this.form.action = `/projects/${this.projectId}`;
            // Update form dataset for other JavaScript that might need it
            this.form.dataset.projectId = this.projectId;
            
            // CRITICAL FIX: Add _method field when transitioning from new to existing
            // This ensures manual form submission works correctly after autosave creates the project
            if (!this.form.querySelector('input[name="_method"]')) {
              const methodInput = document.createElement('input');
              methodInput.type = 'hidden';
              methodInput.name = '_method';
              methodInput.value = 'patch';
              this.form.appendChild(methodInput);
            }
            
            // Update submit button text to "Update" when project becomes persisted
            const submitButton = document.querySelector('button[type="submit"][form="project_form"]');
            if (submitButton) {
              submitButton.textContent = 'Aktualizovať';
            }
          }
        }
        
        this.pendingChanges = false;
        this.lastSaveTime = new Date();
      } else {
        console.log('[autosave:performAutosave] save failed — response not ok:', response.status);
        this.showErrorIndicator();
      }
    } catch (error) {
      console.log('[autosave:performAutosave] save threw error:', error.message);
      this.showErrorIndicator();
    } finally {
      this.isSaving = false;
      console.log('[autosave:performAutosave] finally — isSaving:', this.isSaving, 'pendingChanges:', this.pendingChanges);
    }
  }
  
  setupOnlineOfflineHandlers() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.hideSaveIndicator();
      if (this.pendingChanges) {
        this.performAutosave();
      }
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.showOfflineIndicator();
    });
  }
  
  clearSaveTimeout() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }
  
  showErrorIndicator() {
    this.updateSaveIndicator(this.t('saveFailed'), 'error');
  }
  
  showOfflineIndicator() {
    this.updateSaveIndicator(this.t('offline'), 'offline');
  }
  
  updateSaveIndicator(text, status) {
    const indicator = document.getElementById('autosave-indicator');
    if (indicator) {
      indicator.textContent = text;
      indicator.className = `autosave-indicator ${status}`;
      indicator.style.display = 'block';
    }
  }
  
  hideSaveIndicator() {
    const indicator = document.getElementById('autosave-indicator');
    if (indicator) {
      indicator.style.display = 'none';
    }
  }
  
  createSaveIndicator() {
    // Check if indicator already exists
    if (document.getElementById('autosave-indicator')) {
      return;
    }
    
    const indicator = document.createElement('div');
    indicator.id = 'autosave-indicator';
    indicator.className = 'autosave-indicator';
    indicator.style.display = 'none';
    
    // Insert into sharing sidebar if available, otherwise into form
    const container = document.querySelector('.sharing-sidebar') || this.form;
    if (container) {
      container.appendChild(indicator);
    }
  }
  
  // Public method to clear all autosave state before a deliberate form submission.
  // Called by save/publish buttons so beforeunload never fires the confirmation dialog.
  // This is the primary mechanism — the submit event listener is a secondary safety net.
  prepareForSubmit() {
    console.log('[autosave:prepareForSubmit] clearing all autosave state. pendingChanges was:', this.pendingChanges, 'manualSaveRequired was:', this.manualSaveRequired);
    this.pendingChanges = false;
    this.manualSaveRequired = false;
    this.clearSaveTimeout();
    this.hideSaveIndicator();
  }

  // Public method to manually save
  forceSave() {
    if (this.pendingChanges) {
      this.clearSaveTimeout();
      this.performAutosave();
    }
  }
  
  // Public method to check if there are unsaved changes
  hasUnsavedChanges() {
    return this.pendingChanges || this.manualSaveRequired;
  }
}

// Initialize autosave for project forms
document.addEventListener('DOMContentLoaded', () => {
  const projectForm = document.getElementById('project_form');
  if (projectForm) {
    const projectId = projectForm.dataset.projectId;
    // DEFERRED CREATION: Initialize autosave even without project ID (for new projects)
    // Project ID will be set after first successful autosave
    window.autosaveManager = new AutosaveManager('project_form', projectId || null);
    console.log('[autosave:init] AutosaveManager initialized. projectId:', projectId || '(new project)', 'isPublished:', window.autosaveManager.isPublished, 'manualSaveRequired:', window.autosaveManager.manualSaveRequired, 'requestSubmit supported:', typeof projectForm.requestSubmit === 'function');
  }
});

// Export for potential use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AutosaveManager;
}